import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { pmsKeys } from '../queryKeys';
import { rbacApi } from './rbac.api';
import type {
  AssignUserRolePayload,
  CreateRoleTemplatePayload,
  UpdateRoleTemplatePayload,
} from '../types';

/**
 * The signed-in user's PMS role and effective keys.
 *
 * Long `staleTime` with an explicit invalidate on every role edit: permissions
 * change rarely but must change IMMEDIATELY when an admin edits them, and polling
 * a permission set on an interval is both wasteful and still stale.
 */
export function usePmsMyAccess(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: pmsKeys.myAccess(),
    queryFn: () => rbacApi.getMyAccess(),
    enabled: options.enabled ?? true,
    staleTime: 5 * 60 * 1000,
    // A failure here must NOT be retried into a spinner forever — the provider
    // treats "unresolved" as "do not gate", so one quick retry then stop.
    retry: 1,
  });
}

export function usePmsRoleTemplates() {
  return useQuery({
    queryKey: pmsKeys.roleTemplates(),
    queryFn: () => rbacApi.listRoles(),
    staleTime: 60_000,
  });
}

export function usePmsUserRoles() {
  return useQuery({
    queryKey: pmsKeys.userRoles(),
    queryFn: () => rbacApi.listUserRoles(),
    staleTime: 60_000,
  });
}

/** Create a custom role. Returns (and caches) the full re-ordered template list. */
export function useCreatePmsRoleTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateRoleTemplatePayload) => rbacApi.createRole(data),
    onSuccess: (rows) => {
      qc.setQueryData(pmsKeys.roleTemplates(), rows);
      qc.invalidateQueries({ queryKey: pmsKeys.myAccess() });
      qc.invalidateQueries({ queryKey: pmsKeys.userRoles() });
    },
  });
}

/**
 * Delete a custom role. Members on it fall back to the default server-side, so the
 * user-role list and the caller's own access can both change.
 */
export function useDeletePmsRoleTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (roleKey: string) => rbacApi.deleteRole(roleKey),
    onSuccess: (rows) => {
      qc.setQueryData(pmsKeys.roleTemplates(), rows);
      qc.invalidateQueries({ queryKey: pmsKeys.myAccess() });
      qc.invalidateQueries({ queryKey: pmsKeys.userRoles() });
    },
  });
}

/** Editing a template can change the EDITOR's own access, so `myAccess` refetches. */
export function useUpdatePmsRoleTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ roleKey, data }: { roleKey: string; data: UpdateRoleTemplatePayload }) =>
      rbacApi.updateRole(roleKey, data),
    onSuccess: (rows) => {
      qc.setQueryData(pmsKeys.roleTemplates(), rows);
      qc.invalidateQueries({ queryKey: pmsKeys.myAccess() });
      qc.invalidateQueries({ queryKey: pmsKeys.userRoles() });
    },
  });
}

/**
 * Pause / resume a member's PMS access.
 *
 * Invalidates rather than writing the cache directly: the endpoint returns the
 * one changed row, and a deactivation can also change whether that row still
 * belongs in the list at all — so a refetch is the honest read.
 */
export function useSetPmsUserAccess() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ membershipId, active }: { membershipId: string; active: boolean }) =>
      rbacApi.setUserAccess(membershipId, active),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: pmsKeys.userRoles() });
      // An admin can pause THEMSELVES — their own rail must react.
      qc.invalidateQueries({ queryKey: pmsKeys.myAccess() });
    },
  });
}

/**
 * Set (or clear) a member's EVALUATOR flag.
 *
 * Invalidates `userRoles` (the badge on the Users table reads `can_evaluate`
 * from that row) — and nothing else: evaluators are frozen onto a run when it
 * is published, so flipping the flag never rewrites work already in flight.
 */
export function useSetPmsEvaluatorFlag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ membershipId, isEvaluator }: { membershipId: string; isEvaluator: boolean }) =>
      rbacApi.setEvaluatorFlag(membershipId, isEvaluator),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: pmsKeys.userRoles() });
    },
  });
}

/** Revoke a member's PMS grant. See `rbacApi.revokeUserAccess` on the scope. */
export function useRevokePmsUserAccess() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ membershipId }: { membershipId: string }) =>
      rbacApi.revokeUserAccess(membershipId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: pmsKeys.userRoles() });
      qc.invalidateQueries({ queryKey: pmsKeys.myAccess() });
    },
  });
}

export function useAssignPmsUserRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ membershipId, data }: { membershipId: string; data: AssignUserRolePayload }) =>
      rbacApi.assignUserRole(membershipId, data),
    onSuccess: (rows) => {
      qc.setQueryData(pmsKeys.userRoles(), rows);
      // An admin can reassign THEMSELVES — their own rail must react.
      qc.invalidateQueries({ queryKey: pmsKeys.myAccess() });
    },
  });
}

/**
 * Replace one member's permission overrides.
 *
 * `myAccess` is NOT invalidated here: the server refuses a self-target outright,
 * so the caller's own access cannot have changed. Invalidating anyway would
 * suggest it might.
 */
export function useUpdatePmsUserOverrides() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      membershipId,
      overrides,
    }: {
      membershipId: string;
      overrides: Record<string, boolean>;
    }) => rbacApi.updateUserOverrides(membershipId, overrides),
    onSuccess: (rows) => qc.setQueryData(pmsKeys.userRoles(), rows),
  });
}
